import { useViewList } from "@/context/ViewListContext";
import { useGoBack } from "@/hooks";
import { useAllCategories } from "@vixi/api";
import { useTranslation } from "@vixi/localization";
import { Category } from "@vixi/types";
import { FC, useMemo } from "react";
import { Controller, FormProvider, UseFormReturn } from "react-hook-form";
import { z } from "zod";
import { Autocomplete } from "../base/autocomplete";
import { Button } from "../base/button";
import { ScreenTooltip } from "../base/tooltip";
import { TextArea, TextInput } from "../input";
import { PageMainContent } from "../layout/AppShell";
import ViewList, { ViewListItem } from "../layout/ViewList";
import { StyledTextLink } from "../links";
import { FieldLabel, FormDescription, FormHeading } from "../typography";
import { manageAccessValidationSchema } from "./ManageAccessForm";
import { Divider } from "../base/divider";

export const demographicValidationSchema = z
  .object({
    category: z.number(),
  })
  .extend(manageAccessValidationSchema.shape);

export type DemographicFormValues = z.infer<typeof demographicValidationSchema>;

const stepTwoValidationSchema = demographicValidationSchema.omit({
  accessBy: true,
});

const stepTwoValidationPassed = (form: DemographicFormValues) => {
  const parsed = stepTwoValidationSchema.safeParse(form);

  return parsed.success;
};

export const mainDetailsValidationSchema = z.object({
  courseName: z.string().trim().min(3),
  courseDescription: z.string().trim().min(10),
  authorDescription: z.string().trim().min(10),
});

export type MainDetailsFormValues = z.infer<typeof mainDetailsValidationSchema>;

interface CourseDetailsFormProps {
  mainDetailsForm: UseFormReturn<MainDetailsFormValues>;
  demographicForm: UseFormReturn<DemographicFormValues>;
  disabled?: boolean;
  onBack: () => void;
  submitButton: (isValid: boolean) => React.ReactNode;
}

export const CourseDetailsForm: FC<CourseDetailsFormProps> = ({
  mainDetailsForm,
  demographicForm,
  onBack,
  submitButton,
}) => {
  const { t, tProp } = useTranslation();

  const viewList = useViewList({
    onPrevious: onBack,
  });

  useGoBack(viewList.prevStep);

  const {
    handleSubmit: mainDetailsHandleSubmit,
    formState: {
      isValid: mainDetailsFormValid,
      errors: mainDetailsErrors,
      touchedFields: _mainDetailsTouchedFields,
    },
    register: mainDetailsRegister,
  } = mainDetailsForm;

  const { control: demographicControl, watch: demographicWatch } = demographicForm;

  const stepValid = [mainDetailsFormValid, stepTwoValidationPassed(demographicForm.watch())];

  const { data: categories } = useAllCategories();
  const categoryAutocompleteProps = useMemo(
    () => ({
      options: categories ?? [],
      getOptionLabel: (cat: Category) => tProp(cat, "name"),
      getOptionKey: (cat: Category) => `${cat.name} ${cat.nameAr}`,
    }),
    [categories, tProp],
  );

  return (
    <PageMainContent psY="none" psX="none" scrollable className="!pt-0">
      <ViewList {...viewList} >
        {/* Course Details */}
        <ViewListItem>
          <div className="!mt-0">
          <FormHeading
          className="flex flex-col justify-center items-center !mt-0"
            heading={"CourseDetailsForm.Title"}
            step={1}
            totalSteps={viewList.stepsCount}
          >
            <FormDescription >
              {t("CourseDetailsForm.Subtitle1")}
              &nbsp;&nbsp;
              <ScreenTooltip variant="outlined" image="/images/screens/course-details.png">
                <StyledTextLink>{t("Action.SeeHow")}</StyledTextLink>
              </ScreenTooltip>
            </FormDescription>

            <FormDescription className="leading-3 ">{t("CourseDetailsForm.Subtitle2")}</FormDescription>
            <Divider className="mt-4 mb-7.5 w-[50%]" />
          </FormHeading>


          {/* Main Form */}
          <form className="flex justify-center items-center" onSubmit={mainDetailsHandleSubmit(viewList.nextStep)}>
            {/* Form fields */}
            <div className="flex flex-col gap-4">
              {/* Course Name Field */}
              <div>
                <FieldLabel>{t("CourseDetailsForm.CourseNameLabel")}</FieldLabel>

                <TextInput
                  placeholder={t("CourseDetailsForm.CourseNamePlaceholder")}
                  {...mainDetailsRegister("courseName")}
                />
              </div>

              {/* Course Description Field */}
              <div>
                <FieldLabel>{t("CourseDetailsForm.CourseDescriptionLabel")}</FieldLabel>
                <TextArea
                  className="w-123.75 max-w-full"
                  rows={5}
                  maxLength={850}
                  {...mainDetailsRegister("courseDescription")}
                  state={!!mainDetailsErrors.courseDescription ? "error" : "default"}
                  helperText={[mainDetailsErrors.courseDescription?.message]}
                />
              </div>

              {/* Author Description Field */}
              <div>
                <FieldLabel>{t("CourseDetailsForm.AuthorDescriptionLabel")}</FieldLabel>
                <TextArea
                  className="w-123.75 max-w-full"
                  rows={5}
                  maxLength={850}
                  showMaxLength
                  {...mainDetailsRegister("authorDescription", {
                    shouldUnregister: false,
                  })}
                  state={!!mainDetailsErrors.authorDescription ? "error" : "default"}
                  helperText={[mainDetailsErrors.authorDescription?.message]}
                />
              </div>
            </div>

            {/* Next button */}
            <Button
              size="large"
              variant="contained"
              color="primary"
              type="submit"
              className="fixed end-25 bottom-16"
              disabled={!stepValid[0]}
            >
              {t("Action.Next")}
            </Button>
          </form>
          </div>
        </ViewListItem>

        {/* Demographic Details */}
        <ViewListItem>
          <div className="flex flex-col justify-center items-center">
          <FormHeading
            heading={"CourseDetailsForm.Title"}
            step={2}
            totalSteps={viewList.stepsCount}
            className="flex flex-col justify-center items-center mt-3.5"
          />
          <Divider className="w-[50%] my-7.5 "/>
          </div>
          <FormProvider {...demographicForm}>
            <div className="flex flex-col gap-7.5 justify-center items-center">
            
              {/* Category Field */}
              <div >
                <FieldLabel >{t("CourseDetailsForm.CategoryLabel")}</FieldLabel>

                <Controller
                  name="category"
                  control={demographicControl}
                  render={({ field: { ref, value, onChange, ...field } }) => (
                    <Autocomplete
                      {...field}
                      {...categoryAutocompleteProps}
                      value={categories?.find((cat) => cat.id === value) ?? null}
                      onChange={(value) => onChange(value?.id)}
                    />
                  )}
                />
              </div>

            
            </div>
          </FormProvider>

          <div className="fixed end-25 bottom-16 flex gap-10">{submitButton(stepValid[1])}</div>
        </ViewListItem>
      </ViewList>
    </PageMainContent>
  );
};
